import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { InfoRow } from 'common/components/InfoRow';
import { FileTable } from 'common/tables';
import { formatDate } from 'common/utils/formatDate';
import { formatPrice } from 'common/utils/formatPrice';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';
import { IBudgetRequestDetailFragment } from 'graphql/types';
import { UserDrawerHeader } from 'user/components/UserDrawerHeader';
import { UserActionBar } from 'user/UserActionBar';
import { OfferEventBasicListItem } from 'offer/components/OfferEventBasicListItem';
import { BudgetApprovalStatusChip } from 'budget/components/BudgetApprovals/BudgetApprovalStatusChip';

import { BudgetRequestLog } from './BudgetRequestLog';

const transKey = 'budgetRequestDetailDrawer';

interface IProps {
    budgetRequest: IBudgetRequestDetailFragment;
}

export const BudgetRequestDetail = ({ budgetRequest }: IProps) => {
    const [translate] = useTranslation();
    const { openGlobalDrawer } = useGlobalDrawer();

    const {
        amount,
        user,
        status,
        offerEvent,
        title,
        group,
        provider,
        externalUrl,
        description,
        startDate,
        endDate,
        creationDate,
        budgetRequestLog,
        files,
    } = budgetRequest;

    const formattedAmount = amount ? `€ ${formatPrice(amount)}` : '';

    const dateRange = [startDate, endDate]
        .filter(Boolean)
        .map((date) => formatDate(date, 'd MMM yyyy'))
        .join(' – ');

    return (
        <>
            <UserDrawerHeader user={user} />

            <UserActionBar userId={user.id} />

            <Stack
                useFlexGap
                divider={<Divider sx={{ mx: { xs: -2, sm: -4 } }} />}
                spacing={3}
                sx={{ px: { xs: 2, sm: 4 }, py: 3 }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                    }}
                >
                    <Typography sx={{ fontWeight: 700 }} variant="h2">
                        {amount
                            ? formattedAmount
                            : translate(`${transKey}.free`)}
                    </Typography>

                    <BudgetApprovalStatusChip status={status} />
                </Box>

                {offerEvent ? (
                    <OfferEventBasicListItem
                        offerEvent={offerEvent}
                        py={0}
                        onClick={() =>
                            openGlobalDrawer({
                                type: IGlobalDrawerType.OfferEvent,
                                itemId: offerEvent.id,
                            })
                        }
                    />
                ) : (
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 700,
                                lineHeight: 1.2,
                                mb: 3,
                            }}
                        >
                            {title}
                        </Typography>

                        <Stack spacing={2}>
                            {!!group && (
                                <InfoRow
                                    description={translate(`${transKey}.group`)}
                                >
                                    {group.name}
                                </InfoRow>
                            )}

                            {!!provider && (
                                <InfoRow
                                    description={translate(
                                        `${transKey}.provider`
                                    )}
                                >
                                    {provider}
                                </InfoRow>
                            )}

                            {!!externalUrl && (
                                <InfoRow
                                    description={translate(`${transKey}.link`)}
                                >
                                    <Link
                                        href={externalUrl}
                                        rel="noopener noreferrer"
                                        target="_blank"
                                    >
                                        {externalUrl}
                                    </Link>
                                </InfoRow>
                            )}

                            {!!dateRange && (
                                <InfoRow
                                    description={translate(`${transKey}.date`)}
                                >
                                    {dateRange}
                                </InfoRow>
                            )}
                        </Stack>
                    </Box>
                )}

                {!!description && (
                    <Typography sx={{ lineHeight: 1.2 }}>
                        {description}
                    </Typography>
                )}

                <BudgetRequestLog
                    creationDate={creationDate}
                    logs={budgetRequestLog}
                    requestedByName={user.name}
                />

                <FileTable
                    inDrawer
                    readonly
                    emptyText={translate(`${transKey}.noFiles`)}
                    id="budgetRequest"
                    items={files}
                />
            </Stack>
        </>
    );
};
